import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI } from '../services/api';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { API } from '../config';
import '../styles/theme.css';
import './PostJob.css';

function PostJob() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [recruiterCompany, setRecruiterCompany] = useState('');
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    company: '',
    department: '',
    employment_type: 'Full-time',
    
    // Location
    city: '',
    country: 'South Africa',
    work_mode: 'On-site',
    
    // Job Details
    description: '',
    responsibilities: '',
    requirements: '',
    preferred_skills: '',
    
    // Salary
    salary_min: '',
    salary_max: '',
    salary_currency: 'ZAR',
    salary_negotiable: false,
    
    // Benefits
    benefits: [],
    
    // Requirements
    experience_level: '',
    education_requirement: '',
    
    // Application
    application_deadline: '',
    required_documents: [],
    contact_email: '',
    portfolio_url: '',
    
    // Screening Questions
    screening_questions: [],
    // Automated Screening
    auto_screen_enabled: false,
    auto_screen_threshold: 60,
    auto_screen_template_id: '',
  });

  const [benefitInput, setBenefitInput] = useState('');
  const [questionInput, setQuestionInput] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const tier = localStorage.getItem('user_tier') || 'starter';
  const isBusiness = tier === 'business';

  // Load recruiter's company name on mount — company field is locked to their profile
  useEffect(() => {
    recruiterAPI.getProfile().then(res => {
      const name = res.data?.company_name || '';
      setRecruiterCompany(name);
      setFormData(prev => ({ ...prev, company: name }));
    }).catch(() => {});
    // Load email templates for auto-screen rejection
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/email-templates/`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : []).then(d => setEmailTemplates(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDocumentToggle = (doc) => {
    setFormData(prev => ({
      ...prev,
      required_documents: prev.required_documents.includes(doc)
        ? prev.required_documents.filter(d => d !== doc)
        : [...prev.required_documents, doc]
    }));
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, benefitInput.trim()]
      }));
      setBenefitInput('');
    }
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const addQuestion = () => {
    if (questionInput.trim()) {
      setFormData(prev => ({
        ...prev,
        screening_questions: [...prev.screening_questions, questionInput.trim()]
      }));
      setQuestionInput('');
    }
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      screening_questions: prev.screening_questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Show preview instead of submitting directly
    setShowPreview(true);
  };

  const handleConfirmPost = async () => {
    setError('');
    setLoading(true);

    try {
      // Format the application deadline to ISO format if it exists
      // Parse as local midnight (not UTC) to avoid timezone-off-by-one-day issues
      let formattedDeadline = null;
      if (formData.application_deadline) {
        const [year, month, day] = formData.application_deadline.split('-').map(Number);
        // Set to end of day (23:59:59) in local time so the full day is included
        const date = new Date(year, month - 1, day, 23, 59, 59);
        formattedDeadline = date.toISOString();
      }

      const jobData = {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        application_deadline: formattedDeadline,
        url: formData.portfolio_url || null,
        auto_screen_template_id: formData.auto_screen_template_id ? parseInt(formData.auto_screen_template_id) : null,
      };

      await recruiterAPI.createJob(jobData);
      navigate('/recruiter/dashboard');
    } catch (err) {
      console.error('Job posting error:', err.response?.data);
      
      // Handle validation errors
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors
          const errorMessages = err.response.data.detail.map(e => 
            `${e.loc.join(' → ')}: ${e.msg}`
          ).join('\n');
          setError(errorMessages);
        } else if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else {
          setError(JSON.stringify(err.response.data.detail));
        }
      } else {
        setError('Failed to post job. Please try again.');
      }
      setShowPreview(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = () => {
    setShowPreview(false);
  };

  // Preview Modal Component
  if (showPreview) {
    return (
      <div className="portal-layout">
        <RecruiterSidebar />
        <main className="portal-main">
          <div className="portal-topbar">
            <span className="topbar-title">Preview Job Posting</span>
          </div>
          <div className="portal-content">
        <div className="preview-header" style={{ marginBottom: '1.5rem' }}>
          <h1>Preview Your Job Posting</h1>
          <p>Review how your job will appear to candidates</p>
        </div>

        <div className="preview-content">
          <div className="job-preview-card">
            {/* Header */}
            <div className="preview-job-header">
              <div className="preview-title-section">
                <h2>{formData.title}</h2>
                <div className="preview-company">
                  <i className="fas fa-building"></i>
                  {formData.company}
                  {formData.department && <span className="preview-dept"> • {formData.department}</span>}
                </div>
              </div>
              <div className="preview-badge">{formData.employment_type || 'Full-time'}</div>
            </div>

            {/* Quick Info */}
            <div className="preview-quick-info">
              {(formData.city || formData.country) && (
                <div className="preview-info-item">
                  <i className="fas fa-map-marker-alt"></i>
                  {formData.city && formData.country ? `${formData.city}, ${formData.country}` : formData.city || formData.country}
                </div>
              )}
              {formData.work_mode && (
                <div className="preview-info-item">
                  <i className="fas fa-laptop-house"></i>
                  {formData.work_mode}
                </div>
              )}
              {(formData.salary_min || formData.salary_max) && (
                <div className="preview-info-item">
                  <i className="fas fa-money-bill-wave"></i>
                  {formData.salary_currency}{' '}
                  {formData.salary_min ? Number(formData.salary_min).toLocaleString() : '0'} - {formData.salary_max ? Number(formData.salary_max).toLocaleString() : '0'}
                  {formData.salary_negotiable ? ' (Negotiable)' : ''}
                </div>
              )}
              {formData.experience_level && (
                <div className="preview-info-item">
                  <i className="fas fa-chart-line"></i>
                  {formData.experience_level}
                </div>
              )}
            </div>

            {/* Description */}
            {formData.description && (
              <div className="preview-section">
                <h3><i className="fas fa-info-circle"></i> About the Role</h3>
                <p className="preview-text">{formData.description}</p>
              </div>
            )}

            {/* Responsibilities */}
            {formData.responsibilities && (
              <div className="preview-section">
                <h3><i className="fas fa-tasks"></i> Responsibilities</h3>
                <div className="preview-text preview-list">
                  {formData.responsibilities.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <div key={idx} className="preview-list-item">{line}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements */}
            {formData.requirements && (
              <div className="preview-section">
                <h3><i className="fas fa-check-circle"></i> Required Qualifications</h3>
                <div className="preview-text preview-list">
                  {formData.requirements.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <div key={idx} className="preview-list-item">{line}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Preferred Skills */}
            {formData.preferred_skills && (
              <div className="preview-section">
                <h3><i className="fas fa-star"></i> Preferred Skills</h3>
                <div className="preview-text preview-list">
                  {formData.preferred_skills.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <div key={idx} className="preview-list-item">{line}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Benefits */}
            {Array.isArray(formData.benefits) && formData.benefits.length > 0 && (
              <div className="preview-section">
                <h3><i className="fas fa-gift"></i> Benefits</h3>
                <div className="preview-tags">
                  {formData.benefits.map((benefit, idx) => (
                    <span key={idx} className="preview-tag">{String(benefit)}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {formData.education_requirement && (
              <div className="preview-section">
                <h3><i className="fas fa-graduation-cap"></i> Education</h3>
                <p className="preview-text">{formData.education_requirement}</p>
              </div>
            )}

            {/* Application Details */}
            <div className="preview-section">
              <h3><i className="fas fa-paper-plane"></i> Application Details</h3>
              {formData.application_deadline && (
                <p className="preview-text">
                  <strong>Deadline:</strong> {new Date(formData.application_deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
              {Array.isArray(formData.required_documents) && formData.required_documents.length > 0 && (
                <p className="preview-text">
                  <strong>Required Documents:</strong> {formData.required_documents.join(', ')}
                </p>
              )}
              {formData.portfolio_url && (
                <p className="preview-text">
                  <strong>Portfolio Required:</strong> <a href={formData.portfolio_url} target="_blank" rel="noreferrer">{formData.portfolio_url}</a>
                </p>
              )}
              {formData.contact_email && (
                <p className="preview-text">
                  <strong>Contact:</strong> {formData.contact_email}
                </p>
              )}
            </div>

            {/* Screening Questions */}
            {Array.isArray(formData.screening_questions) && formData.screening_questions.length > 0 && (
              <div className="preview-section">
                <h3><i className="fas fa-question-circle"></i> Screening Questions</h3>
                <div className="preview-questions">
                  {formData.screening_questions.map((question, idx) => (
                    <div key={idx} className="preview-question">
                      <span className="question-number">{idx + 1}.</span> {String(question)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="error" style={{whiteSpace: 'pre-wrap'}}>
              {error}
            </div>
          )}

          <div className="preview-actions">
            <button type="button" onClick={handleEditPost} className="btn-secondary" disabled={loading}>
              <i className="fas fa-edit"></i> Edit Post
            </button>
            <button type="button" onClick={handleConfirmPost} className="btn-primary" disabled={loading}>
              {loading ? 'Posting...' : <><i className="fas fa-check"></i> Confirm & Post</>}
            </button>
          </div>
        </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Post a New Job</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme"><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/dashboard')}>
              Cancel
            </button>
          </div>
        </div>

        <div className="portal-content">
          <form onSubmit={handleSubmit} className="post-job-form-inner">
        {/* Basic Information */}
        <section className="form-section">
          <h2>Basic Job Information</h2>
          
          <div className="form-row">
            <div className="input-group">
              <label>Job Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Data Analyst, Software Engineer"
              />
            </div>

            <div className="input-group">
              <label>Company Name</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                readOnly
                style={{ background: 'var(--surface-2)', cursor: 'not-allowed', opacity: 0.8 }}
                title="Company name is set from your recruiter profile"
              />
              <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Locked to your registered company</small>
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label>Department</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="IT, Marketing, HR, etc."
              />
            </div>

            <div className="input-group">
              <label>Employment Type *</label>
              <select name="employment_type" value={formData.employment_type} onChange={handleChange} required>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
                <option value="Freelance">Freelance</option>
              </select>
            </div>
          </div>
        </section>

        {/* Location */}
        <section className="form-section">
          <h2>Location</h2>
          
          <div className="form-row">
            <div className="input-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Johannesburg"
              />
            </div>

            <div className="input-group">
              <label>Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="South Africa"
              />
            </div>

            <div className="input-group">
              <label>Work Mode *</label>
              <select name="work_mode" value={formData.work_mode} onChange={handleChange} required>
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
          </div>
        </section>

        {/* Job Description */}
        <section className="form-section">
          <h2>Job Description</h2>
          
          <div className="input-group">
            <label>Overview *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              placeholder="Provide an overview of the role, what the employee will do daily, what team they'll work with..."
            />
          </div>

          <div className="input-group">
            <label>Responsibilities</label>
            <textarea
              name="responsibilities"
              value={formData.responsibilities}
              onChange={handleChange}
              rows="6"
              placeholder="List the main tasks (one per line)&#10;• Analyze business data&#10;• Build reports and dashboards&#10;• Collaborate with product teams"
            />
          </div>
        </section>

        {/* Skills & Qualifications */}
        <section className="form-section">
          <h2>Required Skills & Qualifications</h2>
          
          <div className="input-group">
            <label>Required Skills *</label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              required
              rows="5"
              placeholder="List mandatory requirements (one per line)&#10;• Degree in Computer Science&#10;• Experience with Python&#10;• SQL knowledge"
            />
          </div>

          <div className="input-group">
            <label>Preferred Skills (Optional)</label>
            <textarea
              name="preferred_skills"
              value={formData.preferred_skills}
              onChange={handleChange}
              rows="4"
              placeholder="Nice-to-have skills&#10;• Experience with Machine Learning&#10;• Knowledge of cloud platforms"
            />
          </div>
        </section>

        {/* Salary */}
        <section className="form-section">
          <h2>Salary Information</h2>
          
          <div className="form-row">
            <div className="input-group">
              <label>Minimum Salary</label>
              <input
                type="number"
                name="salary_min"
                value={formData.salary_min}
                onChange={handleChange}
                placeholder="25000"
              />
            </div>

            <div className="input-group">
              <label>Maximum Salary</label>
              <input
                type="number"
                name="salary_max"
                value={formData.salary_max}
                onChange={handleChange}
                placeholder="40000"
              />
            </div>

            <div className="input-group">
              <label>Currency</label>
              <select name="salary_currency" value={formData.salary_currency} onChange={handleChange}>
                <option value="ZAR">ZAR (South African Rand)</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="salary_negotiable"
                checked={formData.salary_negotiable}
                onChange={handleChange}
              />
              Salary is negotiable
            </label>
          </div>

          <div className="input-group">
            <label>Benefits</label>
            <div className="add-item-group">
              <input
                type="text"
                value={benefitInput}
                onChange={(e) => setBenefitInput(e.target.value)}
                placeholder="e.g., Medical aid, Pension, Remote work"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
              />
              <button type="button" onClick={addBenefit} className="btn-add">Add</button>
            </div>
            <div className="items-list">
              {formData.benefits.map((benefit, index) => (
                <div key={index} className="item-tag">
                  {benefit}
                  <button type="button" onClick={() => removeBenefit(index)}>×</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience & Education */}
        <section className="form-section">
          <h2>Experience & Education</h2>
          
          <div className="form-row">
            <div className="input-group">
              <label>Experience Level</label>
              <select name="experience_level" value={formData.experience_level} onChange={handleChange}>
                <option value="">Select level</option>
                <option value="Entry Level">Entry Level</option>
                <option value="Junior">Junior</option>
                <option value="Mid-Level">Mid-Level</option>
                <option value="Senior">Senior</option>
                <option value="Executive">Executive</option>
              </select>
            </div>

            <div className="input-group">
              <label>Education Requirement</label>
              <select name="education_requirement" value={formData.education_requirement} onChange={handleChange}>
                <option value="">Select requirement</option>
                <option value="Not required">Not required</option>
                <option value="Diploma">Diploma</option>
                <option value="Bachelor's degree">Bachelor's degree</option>
                <option value="Master's degree">Master's degree</option>
                <option value="PhD">PhD</option>
              </select>
            </div>
          </div>
        </section>

        {/* Application Details */}
        <section className="form-section">
          <h2>Application Details</h2>
          
          <div className="form-row">
            <div className="input-group">
              <label>Application Deadline</label>
              <input
                type="date"
                name="application_deadline"
                value={formData.application_deadline}
                onChange={handleChange}
              />
            </div>

            <div className="input-group">
              <label>Contact Email</label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="hr@company.com"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Required Documents</label>
            <div className="checkbox-group">
              {[
                'CV',
                'Academic Transcript',
                'Matric Certificate',
                'Degree Certificate',
                'Diploma Certificate',
                'Cover Letter',
                'Reference Letter',
              ].map(doc => (
                <label key={doc}>
                  <input
                    type="checkbox"
                    checked={formData.required_documents.includes(doc)}
                    onChange={() => handleDocumentToggle(doc)}
                  />
                  {doc}
                </label>
              ))}
            </div>
          </div>

          <div className="input-group" style={{ marginTop: '1rem' }}>
            <label>Portfolio URL <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
            <input
              type="url"
              name="portfolio_url"
              value={formData.portfolio_url}
              onChange={handleChange}
              placeholder="https://yourportfolio.com or GitHub link"
            />
            <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Candidates will be asked to provide their portfolio link when applying</small>
          </div>
        </section>

        {/* Screening Questions */}
        <section className="form-section">
          <h2>Screening Questions</h2>
          
          <div className="input-group">
            <label>Add screening questions to filter candidates</label>
            <div className="add-item-group">
              <input
                type="text"
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                placeholder="e.g., Do you have 2+ years Python experience?"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQuestion())}
              />
              <button type="button" onClick={addQuestion} className="btn-add">Add</button>
            </div>
            <div className="items-list">
              {formData.screening_questions.map((question, index) => (
                <div key={index} className="item-tag">
                  {question}
                  <button type="button" onClick={() => removeQuestion(index)}>×</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Automated Screening */}
        <section className="form-section">
          <h2>
            Automated Screening
            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', background: 'rgba(100,15,247,0.12)', color: '#640ff7', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>Business</span>
          </h2>
          {!isBusiness ? (
            <div style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              <i className="fas fa-lock" style={{ marginRight: '0.5rem' }}></i>
              Automated Screening is available on the Business plan.{' '}
              <span style={{ color: '#640ff7', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/pricing')}>Upgrade</span>
            </div>
          ) : (
            <>
              <div className="checkbox-group">
                <label>
                  <input type="checkbox" name="auto_screen_enabled" checked={formData.auto_screen_enabled}
                    onChange={handleChange} />
                  Enable Automated Screening for this job
                </label>
              </div>
              {formData.auto_screen_enabled && (
                <>
                  <div className="input-group" style={{ marginTop: '1rem' }}>
                    <label>Minimum Match Score to Pass ({formData.auto_screen_threshold}%)</label>
                    <input type="range" name="auto_screen_threshold" min="20" max="90" step="5"
                      value={formData.auto_screen_threshold} onChange={handleChange}
                      style={{ width: '100%', accentColor: '#640ff7' }} />
                    <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                      Applicants below {formData.auto_screen_threshold}% will be flagged for your review with a "Below Threshold" badge.
                    </small>
                  </div>
                  <div className="input-group" style={{ marginTop: '1rem' }}>
                    <label>Rejection Email Template (optional)</label>
                    <select name="auto_screen_template_id" value={formData.auto_screen_template_id} onChange={handleChange} className="form-control">
                      <option value="">Use default rejection message</option>
                      {emailTemplates.filter(t => t.category === 'rejection').map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                      This template will be used when you click "Reject & Notify".
                    </small>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/recruiter/dashboard')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Processing...' : 'Preview Job'}
          </button>
        </div>
      </form>
        </div>
      </main>
    </div>
  );
}

export default PostJob;
